import { useState } from 'react';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useVaultActions } from './hooks/use-vault-actions';
import { getDeleteMatterPermissionState } from '../../../utils/vault-utils';
import { ExtendedVaultMatterModel } from './vault-export-page';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface DeleteVaultMatterDialogProps {
  open: boolean;
  selectedMatterIds: string[];
  handleClose: () => void;
  vaultMatters: ExtendedVaultMatterModel[];
  setSelectedMatters: (ids: string[]) => void;
}

export default function DeleteVaultMatterDialog({
  open,
  selectedMatterIds,
  handleClose,
  vaultMatters,
  setSelectedMatters,
}: DeleteVaultMatterDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { changeLogRoles } = useUserSettings();

  const mattersToDelete = vaultMatters.filter((matter) =>
    selectedMatterIds.includes(matter.matterId)
  );

  const { deleteMatters, isDeletingMatters } = useVaultActions();
  const { toolsRoles } = useUserSettings();

  const isDeletionBlocked = mattersToDelete.some(
    () => !getDeleteMatterPermissionState(toolsRoles).canDelete
  );

  const handleDelete = () => {
    if (isDeletionBlocked) return;
    if (
      isAbleToSeeUIElement(changeLogRoles, VaultRoles.DeleteVaultExports.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      deleteMatters(
        { selectedMatterIds, changeLogMessage },
        {
          onSuccess: () => {
            setSelectedMatters([]);
            setOpenChangeLogDialog(false);
            setChangeLogMessage('');
            handleCloseDialog();
          },
        }
      );
    }
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  if (mattersToDelete.length === 0) return null;

  const isMultiple = mattersToDelete.length > 1;

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleCloseDialog}
        title={isMultiple ? 'Delete Vault Matters' : 'Delete Vault Matter'}
        maxWidth="sm"
        dialogContent={
          <Box>
            <Typography>
              {isMultiple ? (
                <>
                  Delete <strong>{mattersToDelete.length} vault matters</strong>
                  ?
                </>
              ) : (
                <>
                  Delete <strong>{mattersToDelete[0]?.name}</strong>?
                </>
              )}
            </Typography>
          </Box>
        }
        primaryButton={{
          title: isMultiple ? 'Delete Matters' : 'Delete Matter',
          action: handleDelete,
          disabled: isDeletingMatters || isDeletionBlocked,
          loading: isDeletingMatters,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (isDeletionBlocked) return;
          deleteMatters(
            { selectedMatterIds, changeLogMessage },
            {
              onSuccess: () => {
                setSelectedMatters([]);
                setOpenChangeLogDialog(false);
                setChangeLogMessage('');
                handleCloseDialog();
              },
            }
          );
        }}
        mutateLoading={isDeletingMatters}
      />
    </>
  );
}
